import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { InboxArrayFilters, InboxFilters, InboxTaskStatus, AIReceptionistFilterStatus } from '../../types';
import { AiReceptionistFilterPopoverSubmenuItem } from './ai-receptionist-filters/ai-receptionist-filter-popover-submenu-item';
import { LocationPhonesFilterPopoverSubmenuItem } from './location-phones-filters';
import { TagsFilterPopoverSubmenuItem } from './tags-filters';
import { TasksFilterPopoverSubmenuItem } from './tasks-filters';

type InboxFilterPopoverMenuItemProps<T extends InboxArrayFilters> = {
  filter: T;
  toggleItems: <V extends string[] = InboxFilters[T]>(values: V) => void;
  selectedItems: InboxFilters[T];
  groupIds?: string[];
  divider?: 'top' | 'bottom';
  closeMenu: () => void;
};

export const InboxFilterPopoverMenuItem = <T extends InboxArrayFilters>({
  filter,
  toggleItems,
  selectedItems,
  groupIds,
  divider,
  closeMenu,
}: InboxFilterPopoverMenuItemProps<T>) => {
  const { getFeatureFlagValue } = useAppFlagStore();
  const { selectedLocationIds } = useAppScopeStore();

  const hasInboxTasksEnabled = getFeatureFlagValue({
    locationIds: groupIds ?? selectedLocationIds,
    flagName: 'nwx:inbox-tasks',
    strategy: 'ALL',
  });

  const hasInboxFusionEnabled = getFeatureFlagValue({
    locationIds: groupIds ?? selectedLocationIds,
    flagName: 'nwx:inbox-fusion',
    strategy: 'ALL',
  });

  if (filter === 'tags') {
    return (
      <TagsFilterPopoverSubmenuItem
        toggleItems={toggleItems}
        selectedTags={selectedItems as string[]}
        groupIds={groupIds}
        divider={divider}
        closeParentMenu={closeMenu}
        trackingId={`${InboxPrefixes.List}-filters-popover-tags`}
      />
    );
  }

  if (filter === 'locationPhones') {
    return (
      <LocationPhonesFilterPopoverSubmenuItem
        toggleItems={toggleItems}
        selectedLocationPhones={selectedItems as string[]}
        divider={divider}
        trackingId={`${InboxPrefixes.List}-filters-popover-location-phones`}
      />
    );
  }

  if (hasInboxTasksEnabled && filter === 'tasks') {
    return (
      <TasksFilterPopoverSubmenuItem
        toggleItems={toggleItems}
        selectedTasks={selectedItems as InboxTaskStatus[]}
        groupIds={groupIds}
        divider={divider}
        trackingId={`${InboxPrefixes.List}-filters-popover-tasks`}
      />
    );
  }

  if (hasInboxFusionEnabled && filter === 'aiReceptionist') {
    return (
      <AiReceptionistFilterPopoverSubmenuItem
        toggleItems={toggleItems}
        selectedAiStatuses={selectedItems as AIReceptionistFilterStatus[]}
        groupIds={groupIds}
        divider={divider}
        trackingId={`${InboxPrefixes.List}-filters-popover-ai-receptionist`}
      />
    );
  }

  return null;
};
